import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { DatePicker, Form, Input, Radio, Select } from 'antd';
import { Link, useLoaderData, useNavigate } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { GenderEnum } from '../../../../../enums/gender.enum';
import { RoleEnum } from '../../../../../enums/role.enum';
import { CreateUserSuccessResponse } from '../../../../../types/api/create-user-success-response';
import { showLoadingAlertMessage } from '../../../../../../../utils/show-loading-alert-message/show-loading-alert-message';
import { SelectOption } from '../../../../../../../types/select-option';
import { networkCall } from '../../../../../../../utils/network-call/network-call';
import { showDefaultErrorMessages } from '../../../../../../../utils/show-default-error-message/show-default-error-messages';
import { AlertMessageState, clearAlertMessage, showAlertMessage } from '../../../../../../../redux/reducer/alert-message.reducer';
import { AlertMessages } from '../../../../../../../enums/alert-messages.enum';
import { DefaultErrorResponse } from '../../../../../../../types/api/error/default-error-response/default-error-respose.type';
import { ValidationErrorResponseData } from '../../../../../../../types/api/error/validation-error-response-data/validation-error-response-data.type.';
import { tranformAPIValidationErrorToAntDValidationMessage } from '../../../../../../../utils/validation/transform-api-validation-errors-to-antd-validation-messsages';
import { setSelectedMenuItemKeys } from '../../../../../../../redux/reducer/antd-layout-info.reducer';
import { GetCompaniesSuccessResponse } from '../../../../../../companies/types/api/get-companies-success-response';
import { CMSButton } from '../../../../../../../components/cms/button/cms-button.';
import { getImageUrl } from '../../../../../../../utils/image-helper/get-image-url';

export const AdminUserAdd = () => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const loaderData: any = useLoaderData();
    const [companies, setCompanies] = useState<SelectOption[]>([]);

    const handleFinish = async (values: any) => {
        showLoadingAlertMessage();

        let employmentDate;
        if (values.employmentDate)
            employmentDate = `${ values.employmentDate.get('year') }-${ values.employmentDate.get('month') + 1 }-${ values.employmentDate.get('date') }`;

        const response = await networkCall('/users', {
            method: 'POST',
            body: {
                email: values.email,
                name: values.name,
                gender: values.gender,
                role: values.role,
                employmentDate,
                companyId: values.companyId
            }
        });

        const messageShowed = showDefaultErrorMessages(response);
        if (messageShowed) return;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 201) {
            const res: AxiosResponse<CreateUserSuccessResponse> = response;

            alertMessageState.alertMessageText = 'Mentés sikeres.';
            alertMessageState.alertMessageType = 'success';
            navigate(`/cms/admin/users/edit/${ res.data.user.id }`);

        } else if (response?.status === 400) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {

                case 1000:
                    const _res: AxiosResponse<DefaultErrorResponse<ValidationErrorResponseData[]>> = response;
                    alertMessageState.alertMessageText = 'Hibásan megadott beviteli mezők.';
                    const validationErrors = tranformAPIValidationErrorToAntDValidationMessage(_res.data.data!);
                    form.setFields(validationErrors);
                    break;
                case 7002:
                    alertMessageState.alertMessageText = 'Nem adható hozzá több "felhasználó", nincs több kiadható 4 jegyű egyedi azonosító.';
                    break;

            }

        }

        dispatch(showAlertMessage(alertMessageState));
    }

    useEffect(() => {
        dispatch(setSelectedMenuItemKeys(['admin-users']));
    }, [dispatch]);

    useEffect(() => {
        if(loaderData.getCompaniesResponse?.status === 200) {
            const res: AxiosResponse<GetCompaniesSuccessResponse> = loaderData.getCompaniesResponse;
            setCompanies(res.data.companies.map(company => ({ label: company.name, value: company.id.toString()})));
            return;
        }

    }, [loaderData]);

    const genderOptions: SelectOption[] = [
        { label: 'nő', value: GenderEnum.female },
        { label: 'férfi', value: GenderEnum.male }
    ];

    const roleOptions: SelectOption[] = [
        { label: 'cégvezető', value: RoleEnum.CompanyAdmin },
        { label: 'karbantartási vezető', value: RoleEnum.Maintainer },
        { label: 'felhasználó', value: RoleEnum.User }
    ];

    return (
        <>
            <div className="page-title">Új felhasználó hozzáadása</div>
            <Form
                form={form}
                onFinish={handleFinish}
                layout='vertical'
            >
    
                <Form.Item
                    label={<b>Név</b>}
                    name='name'
                    rules={[{required: true, message: 'Név megadása kötelező!'}]}
                >
                    <Input className='box-shadow-4-4-8-0 border-0' />
                </Form.Item>

                <Form.Item
                    label={<b>Email cím</b>}
                    name='email'
                    rules={[{required: true, message: 'Email cím megadása kötelező!'}, {type: 'email', message: 'Helytelen email formátum!'}]}
                >
                    <Input className='box-shadow-4-4-8-0 border-0' />
                </Form.Item>

                <Form.Item
                    label={<b>Neme</b>}
                    name='gender'
                    initialValue={GenderEnum.female}
                >
                    <Radio.Group className='custom-radio-group-input' optionType='button' options={genderOptions} buttonStyle='solid' />
                </Form.Item>

                <Form.Item
                    label={<b>Cég</b>}
                    name='companyId'
                    rules={[{required: true, message: 'Cég választása kötelező!'}]}
                >
                    <Select
                        showSearch
                        placeholder="Válasszon céget!"
                        options={companies}
                        className='box-shadow-4-4-8-0'
                    />
                </Form.Item>

                <Form.Item
                    label={<b>Jogosultsági szint</b>}
                    name='role'
                    initialValue={RoleEnum.User}
                >
                    <Radio.Group className='custom-radio-group-input' optionType='button' options={roleOptions} buttonStyle='solid' />
                </Form.Item>

                <Form.Item
                    name="employmentDate"
                    label={<b>Munkába állás ideje</b>}
                >
                    <DatePicker className='box-shadow-4-4-8-0' style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item className='text-end' style={{ marginTop: '35px' }}>
                    <Link to='/cms/admin/users'>
                        <CMSButton text='Vissza' type='white' iconSrc={getImageUrl('back-icon.png')} />
                    </Link>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    <CMSButton text='Mentés' iconSrc={getImageUrl('Save-icon.png')} htmlType='submit' />
                </Form.Item>
                        
            </Form>
        </>
    );
};